import { FC, useEffect, useState } from 'react';

import { ThemeChangeDetail } from '../hooks/use-custom-theme';
import { generateCSSFromPreset, getPresetById, ThemePreset } from '../theme/custom-theme-presets';
import { generateFullThemeFromColor, generateThemeFromColor, getUserThemes } from '../theme/theme-generator';

const STORAGE_KEY = 'bonusly-custom-theme';

/**
 * Get the stored custom theme preference from localStorage
 */
const getStoredTheme = (): string | null => {
	if (typeof window === 'undefined') return null;
	return localStorage.getItem(STORAGE_KEY);
};

/**
 * Resolve a theme ID to a ThemePreset (handles both built-in and user-generated)
 */
const resolveTheme = (themeId: string | null): ThemePreset | null => {
	if (!themeId) return null;

	// Try built-in preset first
	const preset = getPresetById(themeId);
	if (preset) return preset;

	// Check if it's a user-generated theme
	if (themeId.startsWith('user-')) {
		const userThemes = getUserThemes();
		const userTheme = userThemes.find((t) => t.id === themeId);
		if (userTheme) {
			return themeId.startsWith('user-full-')
				? generateFullThemeFromColor(userTheme.baseColor, userTheme.name)
				: generateThemeFromColor(userTheme.baseColor, userTheme.name);
		}
	}

	return null;
};

/**
 * CustomThemeInjector
 *
 * This component injects a <style> tag that overrides Chakra's CSS variables
 * based on the user's selected custom theme preset.
 *
 * It should be placed near the root of the application (e.g., in App.tsx)
 * so that the CSS variables are available globally.
 *
 * Now supports user-generated themes via the theme generator.
 */
export const CustomThemeInjector: FC = () => {
	const [activePreset, setActivePreset] = useState<ThemePreset | null>(() => {
		const storedId = getStoredTheme();
		return resolveTheme(storedId);
	});

	// Listen for theme change events from useCustomTheme hook
	useEffect(() => {
		const handleThemeChange = (event: CustomEvent<ThemeChangeDetail>) => {
			const detail = event.detail;

			if (detail.type === 'clear') {
				setActivePreset(null);
			} else if (detail.type === 'custom' && detail.customTheme) {
				// Direct custom theme from generator
				setActivePreset(detail.customTheme);
			} else if (detail.type === 'preset' && detail.presetId) {
				// Built-in preset
				const preset = getPresetById(detail.presetId);
				setActivePreset(preset ?? null);
			}
		};

		// Also listen for storage events (for cross-tab sync)
		const handleStorageChange = (event: StorageEvent) => {
			if (event.key === STORAGE_KEY) {
				setActivePreset(resolveTheme(event.newValue));
			}
		};

		window.addEventListener('custom-theme-change', handleThemeChange as EventListener);
		window.addEventListener('storage', handleStorageChange);

		return () => {
			window.removeEventListener('custom-theme-change', handleThemeChange as EventListener);
			window.removeEventListener('storage', handleStorageChange);
		};
	}, []);

	// Don't render anything if no custom theme is active
	if (!activePreset) {
		return null;
	}

	// Generate the CSS from the preset overrides
	const css = generateCSSFromPreset(activePreset);

	return (
		<style
			data-custom-theme={activePreset.id}
			// eslint-disable-next-line react/no-danger
			dangerouslySetInnerHTML={{
				__html: `:root, body, [data-theme], [data-color-mode] {\n\t${css}\n}`,
			}}
		/>
	);
};

CustomThemeInjector.displayName = 'CustomThemeInjector';
